import { TransactionExceptionError, TransactionFailedError, TransactionCancelledError } from '@nftx/errors';
import React, { createContext, useReducer, useCallback, useMemo, useContext, useEffect, useRef } from 'react';
import * as core from 'nftx.js';
import { Network, nsync } from 'nftx.js';
import { createPublicClient, http } from 'viem';
import { mainnet, base, sepolia, arbitrum } from 'viem/chains';

const EventsContext = /*#__PURE__*/createContext({
  events: [],
  pushEvent: () => null
});
const EventsProvider = ({
  children
}) => {
  const [events, dispatch] = useReducer((state, action) => {
    switch (action?.type) {
      case 'push':
        return [...state, action.event];
      default:
        return state;
    }
  }, []);
  const pushEvent = useCallback(e => {
    dispatch({
      type: 'push',
      event: {
        id: [e.type, e.createdAt, e.transaction?.hash].join('-'),
        ...e
      }
    });
  }, []);
  const value = useMemo(() => ({
    events,
    pushEvent
  }), [events, pushEvent]);
  return /*#__PURE__*/React.createElement(EventsContext.Provider, {
    value: value
  }, children);
};
const useEvents = () => {
  const {
    events
  } = useContext(EventsContext);
  return events;
};
const useLatestEvent = () => {
  const events = useEvents();
  return events[events.length - 1];
};
const useAddEvent = () => {
  const {
    pushEvent
  } = useContext(EventsContext);
  return pushEvent;
};
function useOnEvent(...args) {
  const callback = args.pop();
  const type = args.pop();
  const types = type == null ? null : Array.isArray(type) ? type : [type];
  const event = useLatestEvent();
  useEffect(() => {
    if (event) {
      if (types == null || types.includes(event.type)) {
        callback(event);
      }
    }
  }, [event]);
}

const getDefaultProvider = network => {
  const chain = (() => {
    switch (network) {
      case Network.Arbitrum:
        return arbitrum;
      case Network.Sepolia:
        return sepolia;
      case Network.Base:
        return base;
      case Network.Mainnet:
      default:
        return mainnet;
    }
  })();
  return createPublicClient({
    chain,
    transport: http()
  });
};
const {
  config
} = core;
const defaultContext = {
  core,
  network: 1,
  provider: null,
  signer: null,
  onError: () => void 0
};
const NftxContext = /*#__PURE__*/createContext(defaultContext);
const NftxProvider = ({
  children,
  network,
  provider,
  signer = defaultContext.signer,
  onError = defaultContext.onError
}) => {
  const value = useMemo(() => {
    return {
      network,
      provider,
      signer,
      core,
      onError
    };
  }, [network, provider, signer, onError]);
  useEffect(() => {
    if (network != null && network != config.network) {
      config.configure({
        network
      });
    }
  }, [network]);
  return /*#__PURE__*/React.createElement(NftxContext.Provider, {
    value: value
  }, /*#__PURE__*/React.createElement(EventsProvider, null, children));
};
const useNftx = () => {
  const ctx = useContext(NftxContext);
  const network = ctx.network ?? config.network;
  const provider = ctx.provider ?? getDefaultProvider(network);
  const signer = ctx.signer;
  const core = ctx.core;
  return {
    network,
    provider,
    signer,
    core
  };
};
const useNetwork = () => useNftx().network;
const useProvider = () => useNftx().provider;
const useSigner = () => useNftx().signer;
const useNftxCore = () => useNftx().core;

/**
 * wraps a promise and returns a tuple of [error, result]
 * @param p Promise
 * @returns [error, result]
 */
const t = p => {
  return p.then(result => {
    return [undefined, result];
  }, err => {
    return [err, undefined];
  });
};

/** Wraps a variable in a promise. If the variable is already a promise, it is left as-is */
const toPromise = async t => t;

const useTransactionReducer = () => {
  return useReducer((state, action) => {
    switch (action?.status) {
      case 'None':
        return {
          status: 'None',
          data: {},
          error: undefined
        };
      case 'PendingSignature':
        return {
          status: 'PendingSignature',
          data: {},
          error: undefined
        };
      case 'Mining':
        return {
          status: 'Mining',
          data: {
            transaction: action.transaction
          },
          error: undefined
        };
      case 'Success':
        return {
          status: 'Success',
          data: {
            transaction: action.transaction,
            receipt: action.receipt
          },
          error: undefined
        };
      case 'Exception':
      case 'Fail':
        if (action.error instanceof TransactionExceptionError) {
          return {
            status: 'Exception',
            data: state.data,
            error: action.error
          };
        } else if (action.error instanceof TransactionFailedError) {
          return {
            status: 'Fail',
            data: state.data,
            error: action.error
          };
        } else if (action.error instanceof TransactionCancelledError) {
          return {
            status: 'None',
            data: state.data,
            error: action.error
          };
        } else {
          return {
            status: action.status,
            data: state.data,
            error: action.error
          };
        }
      default:
        return state;
    }
  }, {
    status: 'None',
    error: undefined,
    data: {}
  });
};

// This is a special case where a transaction has been replaced by another transaction
const isDroppedAndReplaced = e => {
  return e?.code === 'TRANSACTION_REPLACED' && e?.replacement && (e?.reason === 'repriced' || e?.cancelled === false);
};
const emitPendingSignature = ({
  addEvent,
  description,
  dispatch,
  network
}) => {
  dispatch({
    status: 'PendingSignature'
  });
  addEvent({
    type: 'PendingSignature',
    network,
    createdAt: Date.now(),
    description
  });
};
const emitMining = ({
  addEvent,
  description,
  dispatch,
  network,
  transaction
}) => {
  dispatch({
    status: 'Mining',
    transaction
  });
  addEvent({
    type: 'Mining',
    network,
    createdAt: Date.now(),
    transaction,
    description
  });
};
const emitSuccess = ({
  addEvent,
  description,
  network,
  receipt,
  transaction,
  dispatch
}) => {
  dispatch({
    status: 'Success',
    receipt,
    transaction
  });
  addEvent({
    type: 'Success',
    createdAt: Date.now(),
    network,
    receipt,
    transaction,
    description
  });
};
const emitFail = ({
  addEvent,
  description,
  error,
  network,
  receipt,
  transaction,
  dispatch
}) => {
  dispatch({
    status: 'Fail',
    error
  });
  addEvent({
    type: 'Fail',
    createdAt: Date.now(),
    network,
    receipt,
    transaction,
    error,
    description
  });
};
const emitException = ({
  addEvent,
  description,
  error,
  network,
  receipt,
  transaction,
  dispatch
}) => {
  dispatch({
    status: 'Exception',
    error
  });
  addEvent({
    type: 'Exception',
    createdAt: Date.now(),
    network,
    receipt,
    transaction,
    error,
    description
  });
};
const createTransaction = async ({
  args,
  fn,
  network
}) => {
  const [txErr, transaction] = await t(fn(args));
  if (txErr) {
    const e = txErr.cause ?? txErr;
    // Exception - preflight - we couldn't even trigger the transaction
    throw new TransactionExceptionError(e, network);
  }
  return transaction;
};
const handleReceiptError = ({
  network,
  receiptErr,
  transaction
}) => {
  // Check if the transaction has been replaced by another
  if (isDroppedAndReplaced(receiptErr)) {
    // If status is 0 it means that the replacement transaction has failed
    if (receiptErr.receipt.status === 0) {
      throw new TransactionFailedError(network, receiptErr, receiptErr.replacement, receiptErr.receipt);
    }
    // The entire transaction has been replaced by another (successful) transaction
    return [receiptErr.replacement, receiptErr.receipt];
  }

  // Fail
  throw new TransactionFailedError(network, receiptErr, transaction, receiptErr.receipt);
};
const waitForTransactionReceipt = async ({
  network,
  transaction: _transaction
}) => {
  let transaction = _transaction;
  const [receiptErr, _receipt] = await t(transaction.wait());
  let receipt = _receipt;
  if (receiptErr) {
    [transaction, receipt] = handleReceiptError({
      network,
      receiptErr,
      transaction
    });
  }
  if (receipt.status === 'reverted') {
    throw new TransactionFailedError(network, {}, transaction, receipt);
  }
  return [transaction, receipt];
};
const updateRequiredBlock = receipt => {
  // We ideally want data to be up to the block for this transaction
  // So we store the block number and any subsequent api calls will
  // be made with "live mode" enabled until the api has caught up
  const txnBlock = Number(receipt.blockNumber);
  // For a bit of leeway we add a buffer to the required block number
  // so the api must be at least this far ahead before we switch back to "api mode"
  const buffer = nsync.getBlockBuffer();
  nsync.setRequiredBlockNumber(txnBlock + buffer);
};
const handleSuccess = async ({
  args,
  opts,
  receipt,
  transaction,
  addEvent,
  network,
  description,
  dispatch
}) => {
  updateRequiredBlock(receipt);
  emitSuccess({
    addEvent,
    network,
    receipt,
    transaction,
    description,
    dispatch
  });

  // Handle onSuccess hook
  await toPromise(opts?.onSuccess?.({
    transaction: transaction,
    receipt
  }, args));
};
const mutate = async ({
  addEvent,
  args,
  dispatch,
  fn,
  network,
  opts
}) => {
  const description = opts?.description || '';
  emitPendingSignature({
    addEvent,
    description,
    dispatch,
    network
  });
  let transaction = await createTransaction({
    args,
    fn,
    network
  });
  let receipt;
  emitMining({
    addEvent,
    description,
    dispatch,
    network,
    transaction
  });

  // The transaction receipt may be dropped and replaced so make sure we have the latest transaction
  // eslint-disable-next-line prefer-const
  [transaction, receipt] = await waitForTransactionReceipt({
    addEvent,
    description,
    network,
    transaction
  });
  await handleSuccess({
    args,
    opts,
    receipt,
    transaction,
    addEvent,
    description,
    network,
    dispatch
  });
  return receipt;
};
const handleError = async ({
  dispatch,
  e,
  globalOnError,
  opts,
  addEvent,
  description,
  network
}) => {
  let error = e;
  const [newError] = await t(toPromise(opts?.onError?.(error)));
  if (newError) {
    error = newError;
  }
  await t(toPromise(globalOnError(error)));
  const emit = error instanceof TransactionFailedError ? emitFail : emitException;
  emit({
    addEvent,
    description,
    error,
    network,
    receipt: error.receipt,
    transaction: error.transaction,
    dispatch
  });
};
const useMutate = ({
  fn: _fn,
  opts: _opts,
  addEvent,
  dispatch,
  network,
  globalOnError
}) => {
  // We use refs to keep the latest function and options without recomputing the callback on every render
  const fnRef = useRef(_fn);
  const optsRef = useRef(_opts);
  fnRef.current = _fn;
  optsRef.current = _opts;
  return useCallback(async args => {
    const fn = fnRef.current;
    const opts = optsRef.current;
    const [e, receipt] = await t(mutate({
      addEvent,
      args,
      dispatch,
      fn,
      network,
      opts
    }));
    if (e) {
      await handleError({
        e,
        opts,
        dispatch,
        globalOnError,
        addEvent,
        description: opts?.description || '',
        network
      });
    }
    return receipt;
  }, [addEvent, dispatch, network]);
};
const useReset = dispatch => {
  return useCallback(() => {
    dispatch({
      status: 'None'
    });
  }, []);
};

/**
 * Wraps a function with some common Contract transaction logic
 * The function is wrapped in notification logic
 * The returned status is only set to loading once the transaction is being mined
 * aka after confirming via metamask.
 * The transaction's .wait() method is automatically called
 * The return value is the transaction receipt
 * */
const useTransaction = (fn, opts) => {
  const addEvent = useAddEvent();
  const {
    onError: globalOnError,
    network
  } = useContext(NftxContext);
  const [{
    status,
    data,
    error
  }, dispatch] = useTransactionReducer();
  const mutate = useMutate({
    fn,
    opts,
    dispatch,
    addEvent,
    network,
    globalOnError
  });
  const reset = useReset(dispatch);
  const meta = {
    status,
    error,
    reset,
    data,
    isIdle: status === 'None',
    isPending: status === 'PendingSignature',
    isException: status === 'Exception',
    isFail: status === 'Fail',
    isMining: status === 'Mining',
    isSuccess: status === 'Success',
    isLoading: status === 'Mining' || status === 'PendingSignature',
    isError: status === 'Fail' || status === 'Exception'
  };
  return [mutate, meta];
};
var useTransaction$1 = useTransaction;

const useApprove = opts => {
  const {
    provider,
    signer,
    network,
    core: {
      approve
    }
  } = useNftx();
  return useTransaction$1(args => approve({
    ...args,
    provider,
    signer,
    network
  }), {
    description: 'Approve',
    ...opts
  });
};
var useApprove$1 = useApprove;

const useBuy = opts => {
  const {
    network,
    provider,
    signer,
    core: {
      buy,
      invalidateVault
    }
  } = useNftx();
  return useTransaction$1(args => buy({
    ...args,
    provider,
    network,
    signer
  }), {
    description: 'Buy',
    ...opts,
    async onSuccess(data, args) {
      await invalidateVault({
        network,
        vaultId: args.quote.methodParameters.vaultId
      });
      return opts?.onSuccess?.(data, args);
    }
  });
};
var useBuy$1 = useBuy;

const useMint = opts => {
  const {
    network,
    provider,
    signer,
    core: {
      mint,
      invalidateVault
    }
  } = useNftx();
  return useTransaction$1(args => mint({
    ...args,
    provider,
    signer
  }), {
    description: 'Mint',
    ...opts,
    async onSuccess(data, args) {
      await invalidateVault({
        vaultId: args.quote.methodParameters.vaultId,
        network
      });
      return opts?.onSuccess?.(data, args);
    }
  });
};
var useMint$1 = useMint;

const useRedeem = opts => {
  const {
    network,
    provider,
    signer,
    core: {
      redeem,
      invalidateVault
    }
  } = useNftx();
  return useTransaction$1(args => redeem({
    ...args,
    provider,
    signer
  }), {
    description: 'Redeem',
    ...opts,
    async onSuccess(data, args) {
      await invalidateVault({
        vaultId: args.quote.methodParameters.vaultId,
        network
      });
      return opts?.onSuccess?.(data, args);
    }
  });
};
var useRedeem$1 = useRedeem;

const useSell = opts => {
  const {
    network,
    provider,
    signer,
    core: {
      sell,
      invalidateVault
    }
  } = useNftx();
  return useTransaction$1(args => sell({
    ...args,
    network,
    provider,
    signer
  }), {
    description: 'Sell',
    ...opts,
    async onSuccess(data, args) {
      await invalidateVault({
        vaultId: args.quote.methodParameters.vaultId,
        network
      });
      return opts?.onSuccess?.(data, args);
    }
  });
};
var useSell$1 = useSell;

const useSwap = opts => {
  const {
    network,
    provider,
    signer,
    core: {
      swap,
      invalidateVault
    }
  } = useNftx();
  return useTransaction$1(args => swap({
    ...args,
    network,
    provider,
    signer
  }), {
    description: 'Swap',
    ...opts,
    async onSuccess(data, args) {
      await invalidateVault({
        vaultId: args.quote.methodParameters.vaultId,
        network
      });
      return opts?.onSuccess?.(data, args);
    }
  });
};
var useSwap$1 = useSwap;

export { EventsContext, EventsProvider, NftxContext, NftxProvider, useAddEvent, useApprove$1 as useApprove, useBuy$1 as useBuy, useEvents, useLatestEvent, useMint$1 as useMint, useNetwork, useNftx, useNftxCore, useOnEvent, useProvider, useRedeem$1 as useRedeem, useSell$1 as useSell, useSigner, useSwap$1 as useSwap, useTransaction$1 as useTransaction };
